import type { CreateSnapQuoteLineOutput } from '@snap-types/actions/create-snap-quote-line';
import type { SearchPartsResponse } from '@snap-types/actions/search-parts';
import { ServiceCharge } from '@snap-types/models/service-charge';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { priceStrategyUIConfig } from '@ui/config/price';
import { createAsyncThunk } from '@ui/store/create-async-thunk';
import { v4 as uuid } from 'uuid';

import { QuoteBuilderStateType } from '../../types/state';
import { QuoteLineType } from '../../types/state/quote-line';
import { createSnapQuoteLine } from '../endpoints/create-snap-quote-line';
import { actions } from '../reducer';
import { selectQuoteBuilder } from '../selectors/quote-builder';
import { emptyLastWin } from '../utilities/last-win';

type PartSummary = Pick<
  SearchPartsResponse['data'][number],
  'partId' | 'description' | 'type' | 'material' | 'erpDB' | 'createdBySnap'
>;

type ServiceChargeSummary = Pick<ServiceCharge, 'uuid' | 'id' | 'description'>;

function buildOptimisticQuoteLine({
  part,
  serviceCharge,
  quoteBuilder,
  quoteLineUuid,
  lineNumber,
}: {
  part?: PartSummary | null;
  serviceCharge?: ServiceChargeSummary | null;
  quoteBuilder: QuoteBuilderStateType;
  quoteLineUuid: string;
  lineNumber: number;
}): QuoteLineType {
  const priceStrategy = quoteBuilder.priceStrategies[0];

  if (!part && !serviceCharge) {
    throw new Error(
      'Cannot create optimistic quote line: no part or service charge provided',
    );
  }
  if (part && serviceCharge) {
    throw new Error(
      'Cannot create optimistic quote line: both part and service charge provided',
    );
  }

  if (!priceStrategy) {
    throw new Error(
      'Cannot create optimistic quote line: no price strategy available',
    );
  }

  const initialInfoTab = priceStrategyUIConfig[priceStrategy].tabs[0];

  return {
    uuid: quoteLineUuid,
    description: part?.description ?? null,
    lineNumber,
    partId: part?.partId,
    erpDB: part?.erpDB ?? null,
    partCreatedBySnap: part?.createdBySnap ?? false,
    type: part?.type,
    material: part?.material ?? null,
    estQtyOnHand: null,
    lastWin: emptyLastWin(),
    inventory: null,
    priceStrategy,
    discountPrices: null,
    custPriceEffects: null,
    marketPrices: null,
    configuratorSnapshot: null,
    partSpecs: null,
    priceAdjustment: null,
    priceAdjustmentAmount: null,
    priceSegments: [],
    serviceChargeUuid: serviceCharge?.uuid,
    serviceChargeId: serviceCharge?.id,
    serviceChargeDescription: serviceCharge?.description,
    craftProductOptions: null,
    lastWinOrderLineId: null,
    automatable: false,
    leadTimeSuggestionStatus: SnapSuggestionStatus.UNKNOWN,
    suggestedPricesStatus: SnapSuggestionStatus.UNKNOWN,
    partSite: null,
    isInfoVisible: false,
    infoTab: initialInfoTab,
    infoTabOrderHistoryFilterByCustomer: false,
    infoTabQuoteHistoryFilterByCustomer: false,
    infoTabOrderHistoryExactMatch: false,
    infoTabQuoteHistoryExactMatch: false,
    status: 'creating',
    productOptionsChangeUnitPrice: false,
    finishedPartId: part?.partId || '',
    customerPartId: null,
    leadTimeNotes: null,
    specs: null,
    quotePriceUuids: [],
    quotePrices: {},
    snapWorkOrder: null,
    documentIds: [],
    documents: {},
    productOptions: {},
    productOptionsUuids: [],
    productComplexityRanking: undefined,
    leadTimeConfig: null,
    quantityAwareInputs: null,
  } satisfies QuoteLineType;
}

export const createQuoteLineOptimistically = createAsyncThunk<
  CreateSnapQuoteLineOutput,
  { part?: PartSummary | null; serviceCharge?: ServiceChargeSummary | null },
  {
    fulfilledMeta: { optimisticUuid: string };
    rejectedMeta: { optimisticUuid: string };
    rejectValue: unknown;
  }
>(
  'quoteBuilder/createQuoteLineOptimistically',
  async (
    { part, serviceCharge },
    { getState, dispatch, fulfillWithValue, rejectWithValue },
  ) => {
    const state = getState();
    const quoteBuilder = selectQuoteBuilder(state);

    const quoteLineUuid = uuid();
    const lineNumber = quoteBuilder.quoteLineUuids.length + 1;

    const quoteLine = buildOptimisticQuoteLine({
      part,
      serviceCharge,
      quoteBuilder,
      quoteLineUuid,
      lineNumber,
    });

    dispatch(actions.quoteLineCreatedOptimistic({ quoteLine }));

    try {
      const result = await dispatch(
        createSnapQuoteLine.initiate({
          siteCode: quoteBuilder.siteCode,
          quoteUuid: quoteBuilder.uuid,
          partId: part?.partId,
          serviceChargeUuid: serviceCharge?.uuid,
        }),
      ).unwrap();

      return fulfillWithValue(result, { optimisticUuid: quoteLineUuid });
    } catch (error) {
      return rejectWithValue(error, { optimisticUuid: quoteLineUuid });
    }
  },
);
